/**
 * GlobalDocsPage — the one wallet. Every document the user holds, grouped by
 * car, with a final "personal / unsorted" group for anything not bound to a
 * vehicle (a driver's license, or a doc the auto-binder couldn't place). An
 * orphan can be linked to a car right here. Upload is context-free: the doc
 * figures out where it belongs (and a registration creates the car).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listDocuments, listVehicles, confirmDocument } from '@/lib/api';
import type { DocumentSummary, VehicleSummary } from '@/lib/api';
import { docTypeIconName, docTypeLabel, expiryState, formatDocDate } from '@/lib/kol/docs';
import { Icon } from '@/components/kol/Icon';

function cap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
function carName(v: VehicleSummary): string {
  return v.customLabel?.trim() || `${cap(v.oem)} ${cap(v.model)}`;
}

export function GlobalDocsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [docs, setDocs] = useState<DocumentSummary[] | null>(null);
  const [vehicles, setVehicles] = useState<VehicleSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    Promise.all([listDocuments(), listVehicles().catch(() => ({ items: [] as VehicleSummary[] }))])
      .then(([d, v]) => {
        setDocs(d.items);
        setVehicles(v.items);
      })
      .catch(() => setError(t('common.error', { defaultValue: 'משהו השתבש' })));
  }, [t]);

  useEffect(() => load(), [load]);

  const carsWithDocs = vehicles
    .map((v) => ({ vehicle: v, items: (docs ?? []).filter((d) => d.vehicleId === v.id) }))
    .filter((g) => g.items.length > 0);
  const orphans = (docs ?? []).filter((d) => !d.vehicleId);

  const linkToCar = async (docId: string, vehicleId: string) => {
    try {
      await confirmDocument(docId, undefined, vehicleId);
      load();
    } catch {
      setError(t('common.error', { defaultValue: 'משהו השתבש' }));
    }
  };

  return (
    <div className="kol-grain font-kol min-h-screen bg-kol-canvas text-kol-ink flex flex-col relative" dir={i18n.dir()}>
      <div className="relative z-[1] flex items-start justify-between px-[22px] pt-[max(18px,env(safe-area-inset-top))] pb-2">
        <h1 className="font-kol-display text-[26px] font-medium tracking-tight">
          {t('kol.docs.title', { defaultValue: 'המסמכים' })}
        </h1>
        <button
          type="button"
          aria-label={t('common.back', { defaultValue: 'חזרה' })}
          onClick={() => navigate('/kol')}
          className="w-11 h-11 rounded-full border border-kol-line bg-kol-card-warm grid place-items-center text-kol-ink-soft shadow-kol-1"
        >
          <Icon name="arrow" size={20} />
        </button>
      </div>

      <div className="relative z-[1] flex-1 overflow-y-auto px-[22px] pb-2 flex flex-col gap-5">
        {error && (
          <div
            role="alert"
            className="rounded-[20px] bg-kol-clay-soft border border-[#ecd9c9] text-kol-clay-ink p-4 text-[15px]"
          >
            {error}
          </div>
        )}

        {docs === null && !error && (
          <div className="text-kol-ink-faint text-center pt-10">{t('common.loading', { defaultValue: 'טוען…' })}</div>
        )}

        {docs && docs.length === 0 && (
          <div className="pt-10 text-center flex flex-col items-center gap-3.5">
            <span className="w-16 h-16 rounded-[20px] grid place-items-center bg-kol-sage-soft text-kol-pine">
              <Icon name="folder" size={30} />
            </span>
            <p className="text-[17px] text-kol-ink leading-relaxed max-w-[20rem]">
              {t('kol.docs.empty_global', {
                defaultValue:
                  'עדיין אין מסמכים. צלמו רישיון רכב, ביטוח או כל מסמך — נזהה אותו ונשייך אותו לרכב הנכון לבד.',
              })}
            </p>
          </div>
        )}

        {carsWithDocs.map((g) => (
          <section key={g.vehicle.id} className="flex flex-col gap-3">
            <h2 className="text-[14px] font-bold text-kol-ink-soft px-1">{carName(g.vehicle)}</h2>
            {g.items.map((doc) => (
              <DocRow key={doc.id} doc={doc} t={t} onOpen={() => navigate(`/documents/${doc.id}?kol=1`)} />
            ))}
          </section>
        ))}

        {orphans.length > 0 && (
          <section className="flex flex-col gap-3">
            <h2 className="text-[14px] font-bold text-kol-ink-soft px-1">
              {t('kol.docs.personal_group', { defaultValue: 'אישי / לא משויך' })}
            </h2>
            {orphans.map((doc) => (
              <div key={doc.id} className="flex flex-col gap-2">
                <DocRow doc={doc} t={t} onOpen={() => navigate(`/documents/${doc.id}?kol=1`)} />
                {vehicles.length > 0 && (
                  <label className="flex items-center gap-2 ps-1 text-[15px] text-kol-ink-faint">
                    {t('kol.docs.link_to_car', { defaultValue: 'שייך לרכב' })}
                    <select
                      defaultValue=""
                      onChange={(e) => e.target.value && linkToCar(doc.id, e.target.value)}
                      className="flex-1 rounded-xl border border-kol-line-soft bg-kol-card px-3 py-2 text-[14px] text-kol-ink"
                    >
                      <option value="" disabled>
                        {t('kol.docs.choose_car', { defaultValue: 'בחרו רכב…' })}
                      </option>
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.id}>
                          {carName(v)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            ))}
          </section>
        )}

        {docs && docs.length > 0 && (
          <div className="flex items-center justify-center gap-2 text-kol-ink-faint text-[15px] pt-1.5">
            <Icon name="lock" size={15} />
            {t('kol.docs.privacy', { defaultValue: 'המסמכים שלכם פרטיים. רק אתם רואים אותם.' })}
          </div>
        )}
      </div>

      <div className="relative z-[1] px-[22px] pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={() => navigate('/upload/registration?kol=1')}
          className="w-full rounded-[20px] bg-kol-pine text-white font-semibold py-4 text-[16.5px] min-h-[54px] shadow-[0_16px_30px_-18px_rgba(31,80,66,0.75)] flex items-center justify-center gap-2"
        >
          <Icon name="plus" size={20} /> {t('kol.add_document', { defaultValue: 'הוספת מסמך' })}
        </button>
      </div>
    </div>
  );
}

function DocRow({
  doc,
  t,
  onOpen,
}: {
  doc: DocumentSummary;
  t: (k: string, o?: Record<string, unknown>) => string;
  onOpen: () => void;
}) {
  const exp = expiryState(doc.documentExpiresAt);
  const expDate = formatDocDate(doc.documentExpiresAt);
  const processing = doc.ocrStatus === 'PENDING' || doc.ocrStatus === 'PROCESSING';
  const due = exp === 'expired' || exp === 'soon';

  let expLine: { text: string; warn: boolean } | null = null;
  if (exp === 'expired')
    expLine = { text: t('kol.docs.expired', { defaultValue: 'פג תוקף {{d}}', d: expDate }), warn: true };
  else if (exp === 'soon')
    expLine = { text: t('kol.docs.soon', { defaultValue: 'מסתיים {{d}}', d: expDate }), warn: true };
  else if (expDate)
    expLine = { text: t('kol.docs.valid', { defaultValue: 'בתוקף עד {{d}}', d: expDate }), warn: false };

  return (
    <button
      type="button"
      onClick={onOpen}
      className={[
        'w-full text-start flex items-center gap-3.5 rounded-[20px] p-4 min-h-[68px] shadow-kol-1 border active:scale-[0.99] transition-transform',
        due ? 'bg-kol-card border-[#ecd8c6]' : 'bg-kol-card border-kol-line-soft',
      ].join(' ')}
    >
      <span
        className={`w-11 h-11 rounded-[13px] grid place-items-center flex-none ${due ? 'bg-kol-clay-soft text-kol-clay-ink' : 'bg-kol-sage-soft text-kol-pine'}`}
      >
        <Icon name={docTypeIconName(doc.documentType)} size={22} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[16px] font-bold text-kol-ink truncate">{docTypeLabel(doc.documentType, t)}</div>
        <div className="text-[13.5px] mt-0.5">
          {processing ? (
            <span className="text-kol-ink-faint animate-pulse">{t('kol.docs.reading', { defaultValue: 'קורא…' })}</span>
          ) : expLine ? (
            <span className={expLine.warn ? 'text-kol-clay-ink font-medium' : 'text-kol-ink-faint'}>
              {expLine.text}
            </span>
          ) : (
            <span className="text-kol-ink-faint">
              {doc.documentIssuer || t('kol.docs.saved', { defaultValue: 'נשמר' })}
            </span>
          )}
        </div>
      </div>
      <Icon name="chevron" size={18} className="text-kol-ink-faint" />
    </button>
  );
}
